import { createRoot } from 'react-dom/client';
import { useEffect, useState, useRef } from 'react';
import Swal from '@/react/components/Swal'

import ToasterWrapper from '@/react/components/ToasterWrapper';
const { toast } = ToasterWrapper
import axios from 'axios';
import QCUsersModal from '../../components/QCUsersModal';

import { RowSelection, Pagination, Table, ColumnEdit } from '@/react/libraries/FkEditTable/';
import useTimeline from '@/react/hooks/useTimeline';
import TableForm from '@/react/components/TableForm';


// constants
import { COL_TO_PAYLOAD_KEY_MAP, HEADER_NAMES, COLUMNS_THAT_FORCE_RELOAD, API_URL, BASE_URL } from '@/react/pages/qc/base/{utils}/constants';

// functions
import { initStaticPage, initTableHeaders, validateDataOnCellSave, validateAndAddKeysToCellSavePayload, generateDependentDropdown, isNumber } from '@/react/pages/qc/base/{utils}/functions';
import { LabelButtonCustomizeColumns, LabelButtonDownload, LabelButtonRedo, LabelButtonUndo } from '@/react/components/TableActionButtons';

const Page = ({ payload }) => {
	// local variables
	const { country_id, city_id, stage, period_map, visibleColumns, specific_retention_values } = payload

	// react hookes, states & refs
	const { addToUndoStack, undo, redo, isUndoStackEmpty, isRedoStackEmpty, clearTimeline } = useTimeline()

	const [headers, setHeaders] = useState([])
	const [isPageEnabled, setIsPageEnabled] = useState(true)
	const [isControlsEnabled, setIsControlsEnabled] = useState(true)
	const [selectedTableRows, setSelectedTableRows] = useState([])
	const [tableUrl, setTableUrl] = useState(`${API_URL}datatable/qcs?stage=${stage}&country_id=${country_id}&city_id=${city_id}&form_data={}`)

	const tableRef = useRef(null)
	const paginationRef = useRef(null)
	const taxonomyDropdownRef = useRef(null)
	const routingNumberInputRef = useRef(null)
	const customizeColumnButtonRef = useRef(null)


	const onTableDataLoad = () => {
		setTimeout(() => {
			// get the cells of the retention column
			const retentionColumnIndex = tableRef.current.columns.findIndex((col) => col.heading === 'Retention');
			const periodColumnIndex = tableRef.current.columns.findIndex((col) => col.heading === 'Period');
			const rows = tableRef.current.tableBody.querySelectorAll('tr')

			rows.forEach((row) => {
				const retentionCell = row.getCell(retentionColumnIndex);
				const periodCell = row.getCell(periodColumnIndex);

				if (specific_retention_values.includes(retentionCell.getData().text)) {
					periodCell.setActive(false)
					periodCell.setEditable(false)
				}
			})
		}, 100)
	}

	const onDropdownOpen = async ({ cell, column, row }) => {
		generateDependentDropdown({ tableRef, cell, column, row })
	}

	const onCellDataSaveHandler = ({
		cell, column, newData: _newData, previousData: _previousData, row, additionalParams = {},
	}) => {
		const { fromTimeline } = additionalParams
		const dataObj = validateDataOnCellSave({
			cell, column, newData: _newData, previousData: _previousData, fromTimeline, tableRef, specific_retention_values, row
		})

		if (!dataObj) return

		const { newData, previousData } = dataObj


		const payload = {}
		const api = `${BASE_URL}admin/qc/manage-taxonomy/${row.dataset.rowId}`

		if (!validateAndAddKeysToCellSavePayload({
			payload,
			column,
			newData,
			COL_TO_PAYLOAD_KEY_MAP
		})) return

		payload.status = 3

		const onSuccess = async () => {
			COLUMNS_THAT_FORCE_RELOAD.includes(column.heading) && paginationRef.current.fetchData()

			if (!fromTimeline && previousData) {
				// basically saving manually
				addToUndoStack({ rowIndex: row.dataset.index, columnIndex: column.index, previousData, newData, type: 'normal' }, true)
			}
		}

		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			// update the no column
			if (data.status) {

				onSuccess()

				return data.message
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}

		}).catch(err => {
			cell.updateData(previousData, false)

			if (err.response) {
				throw err.response.data.message
			} else {
				throw 'Something went wrong'
			}
		})

		toast.promise(promise, {
			loading: `${fromTimeline ? 'Reverting' : 'Saving'}...`,
			success: x => x,
			error: x => x,
		})

	}

	const applyBulkEdit = (_, undoElement) => {
		const selectedRows = [...selectedTableRows]
		const selectedTaxonomyName = taxonomyDropdownRef.current.getNames();
		const selectedTaxonomyValue = taxonomyDropdownRef.current.getValues();

		let previousColName = ''
		const previousRowData = []

		const dropdownToColumnNameMap = {
			'category': 'Category',
			'subcategory': 'Subcategory',
			'record_type': 'Record Type',
		}

		const updateTableForTaxonomy = (dropdownKey, payload) => {
			const colName = dropdownToColumnNameMap[dropdownKey];
			const colIndex = tableRef.current.columns.findIndex((col) => col.heading === colName);

			selectedRows.forEach((row) => {
				payload[COL_TO_PAYLOAD_KEY_MAP[colName]] = selectedTaxonomyValue[dropdownKey][0];

				previousColName = colName

				previousRowData.push({
					rowIndex: row.dataset.index,
					rowId: row.rowId,
					value: tableRef.current.getCellData(row.dataset.index, colIndex)
				})
			});
		};

		const payload = {}

		payload.status = 3
		payload.fk_qc_ids = selectedRows.map(row => row.rowId)

		if (!undoElement) {
			if (Object.keys(selectedTaxonomyName).length === 0 && !routingNumberInputRef.current.value) return toast.error(
				'Please select a taxonomy/routing number to apply the changes');

			if (selectedRows.length === 0) return toast.error('Please select some rows to apply the changes');


			Object.keys(selectedTaxonomyName).forEach((dropdownKey) => {
				updateTableForTaxonomy(dropdownKey, payload);
			})

			if (routingNumberInputRef.current.value) {
				if (!window.checkRoutingNumberValidity(routingNumberInputRef.current.value, true))
					return toast.error('Please enter a valid routing number');
				payload.routing_number = routingNumberInputRef.current.value
			}

			const api = `${BASE_URL}admin/qc/bulk-taxonomy-assignment`;
			setIsPageEnabled(false)


			const promise = axios.post(api, payload, {
				headers: window.HEADER_OBJ
			})
				.then(({ data }) => {
					if (data.status) {
						// update the undo stack
						// setUndoStack([...undoStack, { previousColName, previousRowData: [...previousRowData], type: 'bulk' }])
						addToUndoStack({ type: 'bulk' }, true)

						// refresh the table
						paginationRef.current.fetchData()

						return data.message
					} else {
						throw {
							response: {
								data: {
									message: data.message
								}
							}
						}
					}
				}).catch(err => {
					if (err.response) {
						throw err.response.data.message
					} else {
						throw 'Something went wrong'
					}
				}).finally(() => setIsPageEnabled(true))

			toast.promise(promise, {
				loading: 'Saving...',
				success: x => x,
				error: x => x,
			})
		}
	}

	const sendForReview = () => {
		const payload = {}
		payload.status = 4

		payload.fk_qc_ids = [...selectedTableRows].map(row => row.rowId)

		setIsPageEnabled(false)
		const api = `${BASE_URL}admin/qc/send-next-stage`
		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			if (data.status) {
				return data.message;
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}
		}).catch(err => {
			if (err.response) {
				throw err.response.data.message
			} else {
				throw "Couldn't send data for review. Please reload your browser"
			}
		}).finally(() => {
			setIsPageEnabled(true)
			paginationRef.current.fetchData()
		})

		toast.promise(promise, {
			loading: 'Sending for review...',
			success: x => x,
			error: x => x,
		})
	}

	const beforeSendForReview = () => {
		const selectedRows = [...selectedTableRows]
		if (selectedRows.length === 0) return toast.error('Please select some rows to send for review')

		// fire a swal!
		Swal.fire({
			title: `Send ${selectedRows.length} row(s) for review?`,
			text: "This will send the selected rows for review to the review team.",
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Yes, send it!'
		}).then((result) => {
			if (result.isConfirmed) {
				sendForReview()
			}
		})
	}

	const handleHistoryOperation = (operation, dataKey) => {
		const lastElement = operation();
		if (lastElement?.[dataKey] && lastElement.type === 'normal') {
			const { rowIndex, columnIndex, [dataKey]: data } = lastElement;
			tableRef.current.updateCell(rowIndex, columnIndex, data, true, { fromTimeline: true });
		} else if (lastElement && lastElement.type === 'bulk') {
			toast.error('Bulk edit cannot be undone/redone, your next undo/redo will be a normal cell edit, if any.')
		}
	};

	const handleUndo = () => handleHistoryOperation(undo, 'previousData');
	const handleRedo = () => handleHistoryOperation(redo, 'newData');

	useEffect(() => {
		initStaticPage({
			taxonomyDropdownRef,
			initROCategoryMultiLayer,
		})
		initTableHeaders({
			API_URL,
			setHeaders,
			HEADER_NAMES,
			visibleColumns,
			period_map,
		})
	}, [])

	return (
		<div className="page-card">
			<h4 className="page-title">Taxonomy Assignment</h4>
			<div disabled={!isPageEnabled || !isControlsEnabled}>
				<TableForm onSubmit={(e, obj) => {
					const form_data = encodeURIComponent(JSON.stringify(obj));
					const url = `${API_URL}datatable/qcs?stage=${stage}&country_id=${country_id}&city_id=${city_id}&form_data=${form_data}`;
					setTableUrl(url)
				}} />
			</div>
			<div>
				<div className="table-controls" disabled={!isControlsEnabled}>
					<div className='mr-auto flex items-center gap-2'>
						<div id="taxonomyDropdown" className="w-full" data-multi-select="false"></div>
						<input className="input-field w-full" placeholder='Routing Number' ref={routingNumberInputRef} />
						<button className="btn btn-wide" onClick={applyBulkEdit}>
							Apply
						</button>
					</div>
					<LabelButtonUndo onClick={handleUndo} disabled={isUndoStackEmpty} />
					<LabelButtonRedo onClick={handleRedo} disabled={isRedoStackEmpty} />
					<LabelButtonCustomizeColumns ref={customizeColumnButtonRef} />
					<LabelButtonDownload href={`${BASE_URL}admin/qc/download?country_id=${country_id}&city_id=${city_id}&stage=${stage}`} />

					<button className="btn btn-wide" onClick={beforeSendForReview}>Send for review</button>
				</div>
				<div>
					<Table headers={headers} onCellDataSave={onCellDataSaveHandler}
						//  onCellEdit={onCellEdit}
						onDropdownOpen={onDropdownOpen} ref={tableRef} className={
							`!h-[calc(100dvh-456px)] !min-w-0 !overflow-auto
						[&_[data-header="Select_Row"]]:sticky [&_[data-header="Select_Row"]]:left-0 [&_[data-header="Select_Row"]]:bg-white [&_[data-header="Select_Row"]]:shadow-[inset_-1px_0px_0px_0px_#00000014]

						[&_[data-header="Select_All"]]:sticky [&_[data-header="Select_All"]]:left-0 [data-header="Select_All"]]:top-0 [&_[data-header="Select_All"]]:bg-white [&_[data-header="Select_All"]]:z-20 [&_[data-header="Select_All"]]:shadow-[inset_-1px_0px_0px_0px_#00000014]

						[&_thead_th[data-header="Action"]]:!text-center
						[&_td[data-header="Legal_Reference_link"]]:break-all [&_td[data-header="What_to_Store"]]:min-w-[250px]`
						} />
					<RowSelection storageObject={setSelectedTableRows} />
					<Pagination className={'pt-6 pr-11'} url={tableUrl} offsetQueryParameter={"start"} lengthQueryParameter={"length"} ref={paginationRef} onPaginate={() => {
						clearTimeline()
					}} onLoad={onTableDataLoad} />
					<ColumnEdit buttonRef={customizeColumnButtonRef} metaKey="qc_columns" />
				</div>
			</div>
		</div>
	);
}

function initPage(selector, payload) {
	createRoot(document.querySelector(selector)).render(
		<Page payload={payload} />
	);
}

export default initPage;
